/**
 * Pure functions for UI-only translation of terminology in TabCheck-CMS.
 * This ensures legacy DB values (subscriber, administrator, member) remain unchanged
 * while modernizing the customer-facing wording.
 */

/** Customer-facing label for the account-management user listing area (routes unchanged). */
export const USER_MANAGEMENT_LABEL = "User Management";

/**
 * Translates database role values to their customer-facing names.
 * DB values: subscriber, administrator, admin, member, user
 * UI values: Super Admin (Subscriber), Admin, Contributor
 */
export function translateRole(role: string | null | undefined): string {
  if (!role) return role ?? "";
  const normalized = role.toLowerCase().trim();
  switch (normalized) {
    case "subscriber":
      return "Super Admin (Subscriber)";
    case "administrator":
    case "admin":
      return "Admin";
    case "member":
    case "user":
      return "Contributor";
    default:
      return role;
  }
}

/**
 * Translates checklist labels to their modernized terms.
 * Global Checklist -> Trade Template
 * Master Trade Modules -> Trade Templates
 */
export function translateChecklistLabel(text: string | null | undefined): string {
  if (!text) return text ?? "";
  const normalized = text.toLowerCase().trim();
  if (normalized === "global checklist") {
    return "Trade Template";
  }
  if (normalized === "master trade modules") {
    return "Trade Templates";
  }
  if (normalized === "master trade module") {
    return "Trade Template";
  }
  return text;
}

/** Normalizes legacy CMS copy for runtime UI (DB values unchanged). */
export function translateUiCopy(text: string | null | undefined): string {
  if (!text) return text ?? "";
  let result = translateChecklistLabel(text);
  result = result.replace(/\bRegister Trade\b/gi, "Add Checklist");
  result = result.replace(/\bTrade Name\b/gi, "Checklist Name");
  result = result.replace(/\bChecklist Super Admin\b/gi, "Super Admin (Subscriber)");
  result = result.replace(/\bSUPER ADMIN\s*\/\s*OWNER\b/gi, "Super Admin (Subscriber)");
  result = result.replace(/\bOWNER\b/g, "Super Admin (Subscriber)");
  return result;
}

/**
 * Resolves the "Back" button text dynamically based on the origin URL path.
 *
 * Rules:
 * - /owner-admin/account-management -> User Management
 * - /owner-admin/projects/:id -> Checklist Workspace
 * - /owner-admin/projects -> Checklist Management
 * - /owner-admin/subscribers/:id -> Subscriber Workspace
 * - /owner-admin/subscribers -> Super Admin (Subscriber)
 *
 * Fallback:
 * - isSuperAdmin ? "Super Admin (Subscriber)" : USER_MANAGEMENT_LABEL
 */
export function resolveBackLabel(
  from: string | null | undefined,
  isSuperAdmin: boolean
): string {
  if (!from) {
    return isSuperAdmin ? "Super Admin (Subscriber)" : USER_MANAGEMENT_LABEL;
  }

  if (from.startsWith("/owner-admin/account-management")) {
    return USER_MANAGEMENT_LABEL;
  }
  // Order matters: projects/:id before projects
  if (from.startsWith("/owner-admin/projects/")) {
    return "Checklist Workspace";
  }
  if (from.startsWith("/owner-admin/projects")) {
    return "Checklist Management";
  }
  // Order matters: subscribers/:id before subscribers
  if (from.startsWith("/owner-admin/subscribers/")) {
    return "Subscriber Workspace";
  }
  if (from.startsWith("/owner-admin/subscribers")) {
    return "Super Admin (Subscriber)";
  }

  return isSuperAdmin ? "Super Admin (Subscriber)" : USER_MANAGEMENT_LABEL;
}

